import { FormStepProps } from "../SwiperStepperActions";
import { useHookForm } from "mui-react-hook-form-plus";
import React, { useState } from "react";
import {
  Box,
  Button,
  Chip,
  Collapse,
  DialogContent,
  DialogTitle,
  FormControl,
  FormLabel,
  Tabs,
  Typography,
} from "@mui/material";
import FormStep from "../../modernTS/Forms/FormStep";
import { useFieldArray } from "react-hook-form";
import { Add, PendingActions } from "@mui/icons-material";
import { useSalesareaCtx } from "../../modernTS/salesarea/SalesareaContext";
import usePickTimeschedule from "../../modernTS/JUI/Timeschedules/usePickTimeschedule";
import { grey } from "@mui/material/colors";
import Tab from "@mui/material/Tab";
import { UpsellPagesData } from "../../../../store/api/upsellApi";
import { UpsellFlowPages } from "./UpsellFlow";

function Page({ flowIndex, hookForm, activeTab }: { flowIndex: number; hookForm: any; activeTab: number }) {
  const {
    fields: pages,
    append,
    move,
    remove,
  } = useFieldArray({
    name: `flows.${flowIndex}.pages`,
    control: hookForm.control,
  });
  return (
    <UpsellFlowPages
      droppableId={`flow-pages-create-${flowIndex}`}
      fieldArrayNamePrefix={`flows.${flowIndex}`}
      hidden={flowIndex !== activeTab}
      onAppend={() =>
        append({
          name: {},
          description: {},
          upsell_page_variant: "suggestion",
          products: [],
        })
      }
      onMove={(from, to) => move(from, to)}
      onPageDeleted={({ pageIndex }) => {
        remove(pageIndex);
      }}
      onProductsAdded={(newProducts, pageIndex) => {
        const products = [...hookForm.getValues(`flows.${flowIndex}.pages.${pageIndex}.products`)].concat(newProducts);
        hookForm.setValue(`flows.${flowIndex}.pages.${pageIndex}.products`, products);
      }}
      onProductsRemoved={(productIds, pageIndex) => {
        const products = [...hookForm.getValues(`flows.${flowIndex}.pages.${pageIndex}.products`)].filter(
          ({ id }) => !productIds.includes(id)
        );
        hookForm.setValue(`flows.${flowIndex}.pages.${pageIndex}.products`, products);
      }}
      pages={pages}
      startOpen={true}
    />
  );
}

export function UpsellAddPageStep(formStepProps: FormStepProps) {
  const hookForm = useHookForm<UpsellPagesData>({
    defaultValues: {
      flows: [
        {
          time_schedule: undefined,
          pages: [
            {
              name: {},
              description: {},
              upsell_page_variant: "suggestion",
              products: [],
            },
          ],
        },
      ],
    },
  });
  const { watch, setValue } = hookForm;
  const {
    fields: flows,
    append,
    update,
  } = useFieldArray({
    name: "flows",
    control: hookForm.control,
    keyName: "genid",
  });

  const [activeTab, setActiveTab] = useState<number>(-1);

  const timeSchedule = watch("flows.0.time_schedule");
  const { id } = useSalesareaCtx();

  const openTimeschedulePicker = usePickTimeschedule(id);

  const shouldShowScheduleTabs = timeSchedule != null && timeSchedule !== "ALWAYS";
  return (
    <FormStep form={hookForm} {...formStepProps}>
      <DialogTitle>
        <Typography variant={"h4"}>Configure your pages</Typography>
        <Typography variant={"body1"}>You can display different pages for the timeschedules that you pick</Typography>
      </DialogTitle>
      <DialogContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        {timeSchedule === undefined ? (
          <FormControl sx={{ width: "100%" }}>
            <FormLabel>When should this upsell be active?</FormLabel>
            <Box sx={{ display: "grid", gridTemplateColumns: "1fr auto 1fr", gap: 2, alignItems: "center" }}>
              <Button
                onClick={() => {
                  setValue("flows.0.time_schedule", "ALWAYS");
                  setActiveTab(0);
                }}
                variant={"outlined"}
              >
                Upsell is always active
              </Button>
              <Typography>or</Typography>
              <Button
                onClick={async () => {
                  const timeSchedule = await openTimeschedulePicker();
                  update(0, { ...flows[0], time_schedule: timeSchedule });
                  setActiveTab(0);
                }}
                variant={"outlined"}
              >
                Pick a time schedule
              </Button>
            </Box>
          </FormControl>
        ) : timeSchedule === "ALWAYS" ? (
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "1fr auto",
              gap: 2,
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <FormLabel htmlFor={"timeschedule"}>Time schedule</FormLabel>
            <Chip id={"timeschedule"} label={"Always active"} />
          </Box>
        ) : null}
        <Collapse in={shouldShowScheduleTabs}>
          {shouldShowScheduleTabs ? (
            <Box sx={{ borderBottom: 1, borderColor: "divider" }}>
              <Tabs onChange={(e, newTab) => setActiveTab(newTab)} value={activeTab}>
                {flows.map(({ time_schedule }) => (
                  <Tab
                    key={time_schedule.id}
                    label={
                      <>
                        <PendingActions />
                        {`${
                          time_schedule != null &&
                          time_schedule !== "ALWAYS" &&
                          time_schedule.name &&
                          time_schedule.name.length > 0
                            ? time_schedule.name
                            : "Unnamed"
                        }`}
                      </>
                    }
                  />
                ))}
                <Button
                  onClick={async function () {
                    const time_schedule = await openTimeschedulePicker({
                      disableItem: (time_schedule) => {
                        return flows.some(
                          (f) =>
                            f.time_schedule && f.time_schedule !== "ALWAYS" && f.time_schedule.id === time_schedule.id
                        );
                      },
                    });
                    append({
                      time_schedule,
                      pages: [
                        {
                          name: {},
                          description: {},
                          upsell_page_variant: "suggestion",
                          products: [],
                        },
                      ],
                    });
                    setActiveTab(flows.length);
                  }}
                  sx={{ color: grey[600] }}
                  variant={"text"}
                >
                  <Add /> Add another schedule
                </Button>
              </Tabs>
            </Box>
          ) : null}
        </Collapse>
        {flows.map((flow, flowIndex) => (
          <Page activeTab={activeTab} flowIndex={flowIndex} hookForm={hookForm} />
        ))}
      </DialogContent>
    </FormStep>
  );
}
